import {
  createContext,
  Dispatch,
  ReactElement,
  ReactNode,
  SetStateAction,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'

import { useAccounts } from '@modules-deprecated/app/accounts'
import { Account } from '@modules-deprecated/app/accounts/types'
import { useUserOrganization } from '@modules-deprecated/app/organization'
import { DifferenceType } from '@modules-deprecated/bankReconciliation/types'

import { useCurrencies } from '../../../hooks'
import { useAccount } from '../../accounts/hooks/useAccount'
import { useBankAccountByBookkeepingAccountId } from '../../bankAccounts/hooks/useBankAccountByBookkeepingAccountId'
import { BillType } from '../../bills/enums/billType'
import { EQUIVALENT_EXCHANGE_RATE } from '../constants'
import { PayableSubject } from '../types'
import { getTypeFromPayableSubject } from '../utils'
import { calculateExchangeRateUsingDKKRates } from '../utils/calculateExchangeRateUsingDKKRates'

interface ContextState {
  exchangeRate: number
  isCrossCurrency: boolean
  modalId: string
  paymentDate: Date | undefined | null
  reconciliationStartDate?: string | null
  selectedAccount?: Account
  selectedAccountId: string
  selectedDifferenceType?: DifferenceType
  setPaymentDate: Dispatch<SetStateAction<Date | undefined | null>>
  setSelectedAccountId: Dispatch<SetStateAction<string>>
  setSelectedDifferenceType: Dispatch<SetStateAction<DifferenceType | undefined>>
  subjectCurrencyId?: string | null
  isPartialOrOverPayment: boolean
  setIsPartialOrOverPayment: Dispatch<SetStateAction<boolean>>
}

const PaymentModalContext = createContext<ContextState | undefined>(undefined)

interface PaymentModalContextContextProps {
  subject: PayableSubject
  children?: ReactNode
  modalId: string
}

export const PaymentModalContextProvider = ({
  subject,
  children,
  modalId,
}: PaymentModalContextContextProps): ReactElement => {
  const { organization } = useUserOrganization()
  const [selectedDifferenceType, setSelectedDifferenceType] = useState<DifferenceType>()
  const [selectedAccountId, setSelectedAccountId] = useState<string>('')
  const { accounts = [] } = useAccounts()
  const [paymentDate, setPaymentDate] = useState<Date | undefined | null>(undefined)
  const { account: selectedAccount } = useAccount(selectedAccountId)
  const { reconciliationStartDate } = useBankAccountByBookkeepingAccountId(selectedAccountId)
  const { data: currencyStates } = useCurrencies()
  const [isPartialOrOverPayment, setIsPartialOrOverPayment] = useState(false)

  const subjectCurrencyId = subject?.currencyId
  const exchangeRate = useMemo(() => {
    if (!subjectCurrencyId || !selectedAccount?.currencyId) {
      return EQUIVALENT_EXCHANGE_RATE
    }
    return calculateExchangeRateUsingDKKRates({
      currencyStates,
      sourceCurrencyId: subjectCurrencyId,
      targetCurrencyId: selectedAccount?.currencyId,
    })
  }, [currencyStates, selectedAccount?.currencyId, subjectCurrencyId])

  const isCrossCurrency = subjectCurrencyId !== selectedAccount?.currencyId

  useEffect(() => {
    const initialAccountId =
      getTypeFromPayableSubject(subject) === BillType.Bill
        ? organization?.defaultBillBankAccountId
        : organization?.defaultInvoiceBankAccountId

    const initialAccountActive = accounts.some((account) => account.id === initialAccountId && !account.isArchived)
    if (initialAccountId && initialAccountActive) {
      setSelectedAccountId(initialAccountId)
    }
  }, [organization?.defaultBillBankAccountId, organization?.defaultInvoiceBankAccountId, subject, accounts])

  return (
    <PaymentModalContext.Provider
      value={{
        subjectCurrencyId,
        exchangeRate,
        isCrossCurrency,
        modalId,
        paymentDate,
        reconciliationStartDate,
        selectedAccount,
        selectedAccountId,
        selectedDifferenceType,
        setPaymentDate,
        setSelectedAccountId,
        setSelectedDifferenceType,
        isPartialOrOverPayment,
        setIsPartialOrOverPayment,
      }}
    >
      {children}
    </PaymentModalContext.Provider>
  )
}

export const usePaymentModal = () => {
  const context = useContext(PaymentModalContext)

  if (!context) {
    throw new Error('PaymentModalContextProvider is missing in the module!')
  }

  return context
}
